import { ListBox, Pagination, Select, Typography } from "@heroui/react";

import { buildPageList } from "@/utils/pagination";

const PER_PAGE_OPTIONS = ["10", "25", "50", "100"];

interface ListFooterProps {
  label: string;
  /** Leave the paging props out for a list that loads every row. */
  page?: number;
  totalPages?: number;
  perPage?: number;
  onPageChange?: (page: number) => void;
  /** The caller goes back to page 1. */
  onPerPageChange?: (perPage: number) => void;
}

export const ListFooter = ({
  label,
  page = 1,
  totalPages = 0,
  perPage,
  onPageChange,
  onPerPageChange,
}: ListFooterProps) => {
  const pages = buildPageList(totalPages, page);
  const showPages = totalPages > 1 && onPageChange != null;
  const showPerPage = perPage != null && onPerPageChange != null;

  return (
    <div className="flex w-full items-center gap-3 px-3 py-2">
      <Typography
        className="flex-1 whitespace-nowrap text-muted"
        type="body-xs"
      >
        {label}
      </Typography>
      {showPages && (
        // HeroUI's Pagination is full width, which squeezed the label and "Per page" onto two lines.
        <Pagination className="w-auto" size="sm">
          <Pagination.Content>
            <Pagination.Item>
              <Pagination.Previous
                aria-label="Previous page"
                isDisabled={page <= 1}
                onPress={() => onPageChange(page - 1)}
              >
                <Pagination.PreviousIcon />
              </Pagination.Previous>
            </Pagination.Item>
            {pages.map((entry, index) =>
              entry === "..." ? (
                <Pagination.Item key={`ellipsis-${index}`}>
                  <Pagination.Ellipsis />
                </Pagination.Item>
              ) : (
                <Pagination.Item key={entry}>
                  <Pagination.Link
                    isActive={entry === page}
                    onPress={() => onPageChange(entry)}
                  >
                    {entry}
                  </Pagination.Link>
                </Pagination.Item>
              ),
            )}
            <Pagination.Item>
              <Pagination.Next
                aria-label="Next page"
                isDisabled={page >= totalPages}
                onPress={() => onPageChange(page + 1)}
              >
                <Pagination.NextIcon />
              </Pagination.Next>
            </Pagination.Item>
          </Pagination.Content>
        </Pagination>
      )}
      <div className="flex flex-1 items-center justify-end gap-2">
        {showPerPage && (
          <>
            <Typography className="whitespace-nowrap text-muted" type="body-xs">
              Per page
            </Typography>
            <Select
              aria-label="Per page"
              className="w-20"
              value={String(perPage)}
              onChange={(value) => {
                if (value != null) onPerPageChange(Number(value));
              }}
            >
              <Select.Trigger>
                <Select.Value />
                <Select.Indicator />
              </Select.Trigger>
              <Select.Popover>
                <ListBox>
                  {PER_PAGE_OPTIONS.map((option) => (
                    <ListBox.Item key={option} id={option} textValue={option}>
                      {option}
                      <ListBox.ItemIndicator />
                    </ListBox.Item>
                  ))}
                </ListBox>
              </Select.Popover>
            </Select>
          </>
        )}
      </div>
    </div>
  );
};
